import axios from 'axios';
import OfflineQueue from './proctor/OfflineQueue';

// Centralized axios instance
const api = axios.create({
  timeout: 30000,
  withCredentials: true
});

// Response interceptor for auth and transient errors
api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const config = err.config;

    // 1. Handle 401 Unauthorized — session expired or server restarted.
    //    (403 is NOT caught here: it means 'authenticated but wrong role',
    //    which should show an access-denied message, not redirect to login.)
    if (err?.response?.status === 401) {
      const path = window.location.pathname;
      const isProctor = path.startsWith('/proctor');
      const isOnLoginPage =
        path.startsWith('/admin/login') || path.startsWith('/proctor/login');

      if (!isOnLoginPage) {
        if (isProctor) {
          // Proctor: in-app modal preserves offline queue state
          window.dispatchEvent(new CustomEvent('auth_unauthorized', { detail: { role: 'proctor' } }));
        } else {
          // Admin: hard redirect — works on every admin page, no listener required
          const next = encodeURIComponent(path + window.location.search);
          window.location.href = `/admin/login?next=${next}`;
        }
      }
    }

    // 2. Handle Offline/Transient Errors for Proctor Queueable Actions
    const isTransient =
      !err?.response ||
      err.response.status >= 500 ||
      [502, 503, 504].includes(err?.response?.status) ||
      err.code === 'ECONNABORTED' ||
      !navigator.onLine;

    if (isTransient && config && config.queueable) {
      try {
        const opId =
          config.headers?.['X-Op-Id'] || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        await OfflineQueue.add({
          opId,
          url: config.url,
          method: config.method.toUpperCase(),
          data: typeof config.data === 'string' ? JSON.parse(config.data) : config.data,
          timestamp: Date.now()
        });

        // Resolve with a synthetic "queued" response so the caller knows to expect a later sync
        return Promise.resolve({
          status: 202,
          data: { status: 'queued', opId },
          headers: {},
          config
        });
      } catch (queueErr) {
        // IndexedDB unavailable (private browsing, storage quota) — notify UI
        window.dispatchEvent(new CustomEvent('queue_unavailable', { detail: { error: queueErr?.message } }));
      }
    }

    return Promise.reject(err);
  }
);

export default api;
